// utils/persistPetSitterFilters.ts
import type { FiltersValues } from '@/modules/PetSitter/searchServiceHelper'
import { useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'

const PET_SITTER_FILTERS_KEY = 'petSitter_filters'
const PET_SITTER_MAP_ZOOM_KEY = 'petSitter_mapZoom'

export const serviceSlugMap = {
  walk: 'promenade',
  sittingAtCustomerHome: 'garde-chez-vous',
  sittingAtPetsitterHome: 'garde-chez-eux',
  homeVisit: 'visite',
} as const

export function persistPetSitterFiltersFromSignupStore() {
  const state = useSignUpCallRequestStore.getState()

  // Compute period from formSlots
  let period: { start: string | null; end: string | null } = { start: null, end: null }

  const formSlots = state.service?.formSlots

  if (Array.isArray(formSlots) && formSlots.length > 0) {
    const dateStarts = formSlots.map((slot) => slot?.data?.dateStart).filter((d): d is string => typeof d === 'string')

    const dateEnds = formSlots.map((slot) => slot?.data?.dateEnd).filter((d): d is string => typeof d === 'string')

    if (dateStarts.length > 0) {
      period.start = dateStarts
        .map((d) => new Date(d))
        .reduce((min, d) => (d < min ? d : min))
        .toISOString()
    }

    if (dateEnds.length > 0) {
      period.end = dateEnds
        .map((d) => new Date(d))
        .reduce((max, d) => (d > max ? d : max))
        .toISOString()
    }
  }

  const filters: FiltersValues = {
    sortBy: 'recommender',
    address: state.address ?? null,
    period,
    distance: state.distance ?? { min: 0, max: 20 },

    services:
      Object.keys(serviceSlugMap).find(
        (key) => serviceSlugMap[key as keyof typeof serviceSlugMap] === state.service?.selectedService,
      ) ?? '',

    // Optional: if you want to prefill pets from created animals
    pets: Array.from(
      new Set(
        (state.animals ?? []).map((a) => {
          // adjust to your real species slug values if needed
          if (a.selectedRadio === 'cat') return 'cat'
          if (a.selectedRadio === 'nac') return 'nac'
          return 'dog'
        }),
      ),
    ),

    // Optional: map sizeSlug -> your expected UI slugs: little/medium/big/giant
    petSize: Array.from(new Set((state.animals ?? []).map((a) => a.sizeSlug).filter(Boolean) as string[])),

    features: [],
    petCount: null,
  }

  // Important: same storage format as your useLocalStorage hook (usually JSON)
  localStorage.setItem(PET_SITTER_FILTERS_KEY, JSON.stringify(filters))

  // Optional: keep default zoom or compute it; simplest = keep your default
  if (!localStorage.getItem(PET_SITTER_MAP_ZOOM_KEY)) {
    localStorage.setItem(PET_SITTER_MAP_ZOOM_KEY, JSON.stringify(9.8))
  }
}
